import { ConsiderationCriticalityBadge } from '@/components/badges/ConsiderationCriticalityBadge';
import { ConsiderationStatusBadge } from '@/components/badges/ConsiderationStatusBadge';
import { SubscriptionStatusBadge } from '@/components/badges/SubscriptionStatusBadge';
import { TaskStatusBadge } from '@/components/badges/TaskStatusBadge';
import { useRowClick } from '@/lib/navigation';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    considerationsForConsiderationTypeState,
    considerationTypeState,
    homeState,
    homeTaskSubscriptionsListState,
    tasksForSubscriptionState,
    taskTypesForConsiderationTypeState,
} from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import { formatDate, formatDateTime } from '@/util/dates';
import logger from '@/util/logger';
import { HomeTaskSubscriptionDao } from '@rock/db-admin';
import type { ConsiderationTypeId, Home, HomeId, HomeTaskSubscription, Task } from '@rock/types';
import { todayLocalDate } from '@rock/util';
import { orderBy } from 'lodash';
import { ArrowLeft } from 'lucide-react';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

const log = logger('HomeConsiderationTypeDetailsPage');
const subscriptionDao = new HomeTaskSubscriptionDao();

export const HomeConsiderationTypeDetailsPage: FC = () => {
    const { homeId, typeId } = useParams<{ homeId?: HomeId; typeId?: ConsiderationTypeId }>();

    const [home] = useLoadableAtomValue(useMemo(() => homeState(homeId!), [homeId]));
    const [considerationType] = useLoadableAtomValue(useMemo(() => considerationTypeState(typeId), [typeId]));

    if (!home || !considerationType) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Loading...</CardTitle>
                    <CardDescription>Loading home and consideration type details.</CardDescription>
                </CardHeader>
            </Card>
        );
    }

    return <HomeConsiderationTypeDetailsContent home={home} considerationType={considerationType} />;
};

const HomeConsiderationTypeDetailsContent: FC<{
    home: Home;
    considerationType: { id: ConsiderationTypeId; title: string };
}> = ({ home, considerationType }) => {
    const navigate = useNavigate();
    const homeId = home.id;
    const typeId = considerationType.id;

    const [considerations] = useLoadableAtomValue(
        useMemo(() => considerationsForConsiderationTypeState(typeId), [typeId]),
        [],
    );
    const [subscriptions] = useLoadableAtomValue(
        useMemo(() => homeTaskSubscriptionsListState(homeId), [homeId]),
        [],
    );
    const [taskTypes] = useLoadableAtomValue(
        useMemo(() => taskTypesForConsiderationTypeState(typeId), [typeId]),
        [],
    );

    // Find consideration for this home
    const consideration = useMemo(() => considerations?.find((c) => c.homeId === homeId), [considerations, homeId]);

    // Find subscriptions for this consideration type
    const typeSubscriptions = useMemo(
        () => subscriptions?.filter((s) => s.considerationTypeId === typeId) || [],
        [subscriptions, typeId],
    );

    // Group subscriptions by task type
    const subscriptionsByTaskTypeId = useMemo(() => {
        const map = new Map<string, HomeTaskSubscription>();
        for (const sub of typeSubscriptions) {
            map.set(sub.taskTypeId, sub);
        }
        return map;
    }, [typeSubscriptions]);

    const sortedTaskTypes = useMemo(() => orderBy(taskTypes, (t) => t.title.toLowerCase()), [taskTypes]);

    return (
        <div className="space-y-6">
            {/* Header with back button and title */}
            <div className="flex items-start gap-3">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(-1)}
                    aria-label="Back to considerations"
                    className="mt-1"
                >
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-semibold">{considerationType.title}</h2>
                    <ConsiderationCriticalityBadge criticality={consideration?.criticality} />
                </div>
            </div>

            {/* Consideration Card */}
            <Card>
                <CardHeader>
                    <CardTitle>Consideration</CardTitle>
                </CardHeader>
                <CardContent>
                    {!consideration ? (
                        <div className="text-muted-foreground">
                            No consideration exists for this home and consideration type.
                        </div>
                    ) : (
                        <div className="grid gap-4 md:grid-cols-2">
                            <div>
                                <div className="text-sm font-medium text-muted-foreground mb-1">Status</div>
                                <ConsiderationStatusBadge status={consideration.status} variant="badge" />
                            </div>

                            {consideration.acceptedAt && (
                                <div>
                                    <div className="text-sm font-medium text-muted-foreground mb-1">Accepted At</div>
                                    <div className="text-sm">{formatDateTime(consideration.acceptedAt)}</div>
                                </div>
                            )}
                            {consideration.snoozedAt && (
                                <div>
                                    <div className="text-sm font-medium text-muted-foreground mb-1">Pushed Back At</div>
                                    <div className="text-sm">{formatDateTime(consideration.snoozedAt)}</div>
                                </div>
                            )}
                            <div>
                                <div className="text-sm font-medium text-muted-foreground mb-1">Created At</div>
                                <div className="text-sm">{formatDate(consideration.createdAt)}</div>
                            </div>
                            <div>
                                <div className="text-sm font-medium text-muted-foreground mb-1">Updated At</div>
                                <div className="text-sm">{formatDate(consideration.updatedAt)}</div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Task Type Tabs */}
            {sortedTaskTypes.length > 0 ? (
                <>
                    <div className="flex gap-2 border-b mb-6 overflow-x-auto">
                        {sortedTaskTypes.map((taskType) => {
                            const subscription = subscriptionsByTaskTypeId.get(taskType.id);
                            return (
                                <NavLink
                                    key={taskType.id}
                                    to={`/directory/homes/${homeId}/considerations/${typeId}/tasks/${taskType.id}`}
                                    replace
                                    className={({ isActive }) =>
                                        `px-4 py-2 border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${isActive ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`
                                    }
                                    end
                                >
                                    {taskType.title}
                                    <SubscriptionStatusBadge isSubscribed={!!subscription} size="xs" />
                                </NavLink>
                            );
                        })}
                    </div>

                    <Routes>
                        <Route
                            index
                            element={
                                <Navigate
                                    replace
                                    to={`/directory/homes/${homeId}/considerations/${typeId}/tasks/${sortedTaskTypes[0]?.id}`}
                                />
                            }
                        />
                        <Route
                            path="tasks/:taskTypeId"
                            element={
                                <TaskTypeSubscriptionTab
                                    homeId={homeId}
                                    subscriptionsByTaskTypeId={subscriptionsByTaskTypeId}
                                    taskTypes={sortedTaskTypes}
                                />
                            }
                        />
                    </Routes>
                </>
            ) : (
                <Card>
                    <CardHeader>
                        <CardTitle>No Task Types</CardTitle>
                        <CardDescription>
                            This consideration type doesn't have any associated task types yet.
                        </CardDescription>
                    </CardHeader>
                </Card>
            )}
        </div>
    );
};

const TaskTypeSubscriptionTab: FC<{
    homeId: HomeId;
    subscriptionsByTaskTypeId: Map<string, HomeTaskSubscription>;
    taskTypes: { id: string; title: string }[];
}> = ({ homeId, subscriptionsByTaskTypeId, taskTypes }) => {
    const { taskTypeId } = useParams<{ taskTypeId?: string }>();
    const subscription = subscriptionsByTaskTypeId.get(taskTypeId || '');
    const taskTypeTitleById = useMemo(() => {
        const map = new Map<string, string>();
        for (const tt of taskTypes) {
            map.set(tt.id, tt.title);
        }
        return map;
    }, [taskTypes]);
    const [confirmMoveToNow, setConfirmMoveToNow] = useState(false);
    const [isMoving, setIsMoving] = useState(false);

    const [tasks, tasksLoadable] = useLoadableAtomValue(
        useMemo(
            () => tasksForSubscriptionState({ homeId, subscriptionId: subscription?.id }),
            [homeId, subscription?.id],
        ),
        [],
    );

    const isLoading = tasksLoadable.state === 'loading';
    const hasError = tasksLoadable.state === 'hasError';

    const handleMoveToNow = async () => {
        if (!subscription) return;

        setIsMoving(true);
        try {
            const today = todayLocalDate();
            await subscriptionDao.updateHomeTaskSubscription(homeId, subscription.id, {
                nextTaskDate: today,
            });

            toast.success('Next Task Date Updated', {
                description:
                    'The next task date has been moved to now. The task generation code runs hourly, so it may take a little while before the task is generated.',
            });

            log.info('Successfully moved nextTaskDate to now', {
                homeId,
                subscriptionId: subscription.id,
                nextTaskDate: today,
            });
            setConfirmMoveToNow(false);
        } catch (err) {
            log.error('Error moving nextTaskDate to now', { err, homeId, subscriptionId: subscription.id });
            toast.error('Error Moving Next Task Date', {
                description: 'An unexpected error occurred while updating the next task date.',
            });
        } finally {
            setIsMoving(false);
        }
    };

    return (
        <div className="space-y-6">
            {!subscription ? (
                <Card>
                    <CardHeader>
                        <CardTitle>No Subscription</CardTitle>
                        <CardDescription>This home is not subscribed to this task type.</CardDescription>
                    </CardHeader>
                </Card>
            ) : (
                <>
                    {/* Subscription Details Card */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Subscription Details</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div>
                                    <div className="text-sm font-medium text-muted-foreground mb-2">
                                        Subscription Type
                                    </div>
                                    {subscription.subscription.type === 'one-time' ? (
                                        <Badge variant="secondary">
                                            One-Time
                                            {subscription.subscription.offset
                                                ? ` (+${subscription.subscription.offset.value} ${subscription.subscription.offset.unit})`
                                                : ''}
                                        </Badge>
                                    ) : (
                                        <Badge variant="secondary">
                                            Recurring: Every {subscription.subscription.interval.value}{' '}
                                            {subscription.subscription.interval.unit}
                                            {subscription.subscription.offset
                                                ? ` (+${subscription.subscription.offset.value} ${subscription.subscription.offset.unit})`
                                                : ''}
                                        </Badge>
                                    )}
                                </div>

                                <div className="grid gap-4 md:grid-cols-2">
                                    {subscription.lastGeneratedAt && (
                                        <div>
                                            <div className="text-sm font-medium text-muted-foreground mb-1">
                                                Last Generated
                                            </div>
                                            <div className="text-sm">{formatDate(subscription.lastGeneratedAt)}</div>
                                        </div>
                                    )}
                                    {subscription.nextTaskDate && (
                                        <div>
                                            <div className="text-sm font-medium text-muted-foreground mb-1">
                                                Next Task Date
                                            </div>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <div className="text-sm font-medium text-green-600">
                                                    {formatDate(subscription.nextTaskDate)}
                                                </div>
                                                <Button
                                                    size="sm"
                                                    className="bg-yellow-500 text-white hover:bg-yellow-500/90 focus-visible:ring-yellow-600/20 dark:focus-visible:ring-yellow-600/40"
                                                    onClick={() => setConfirmMoveToNow(true)}
                                                >
                                                    Move to Now
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {subscription.status === 'orphaned' && (
                                    <div>
                                        <Badge variant="outline" className="text-yellow-600">
                                            Orphaned
                                        </Badge>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Task History Card */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Task History</CardTitle>
                            <CardDescription>
                                All tasks generated from this subscription, ordered by due date (most recent first).
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {isLoading ? (
                                <div className="text-muted-foreground">Loading tasks…</div>
                            ) : hasError ? (
                                <div className="text-destructive">
                                    Error loading tasks:{' '}
                                    {tasksLoadable.error instanceof Error
                                        ? tasksLoadable.error.message
                                        : String(tasksLoadable.error)}
                                </div>
                            ) : !tasks || tasks.length === 0 ? (
                                <div className="text-sm text-muted-foreground">No tasks have been generated yet.</div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Title</TableHead>
                                            <TableHead>Due Date</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Completed</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {tasks.map((task) => (
                                            <TaskHistoryRow
                                                key={task.id}
                                                homeId={homeId}
                                                task={task}
                                                taskTypeTitleById={taskTypeTitleById}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>

                    {/* Move to Now Confirmation Dialog */}
                    <AlertDialog open={confirmMoveToNow} onOpenChange={(open) => setConfirmMoveToNow(open)}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Move Next Task Date to Now?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    Are you sure you want to move the Next Task Date to right now? The "Generate Tasks"
                                    code runs hourly, so it may be a little while before the task actually generates.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel disabled={isMoving}>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={handleMoveToNow} disabled={isMoving}>
                                    {isMoving ? 'Moving...' : 'Move to Now'}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </>
            )}
        </div>
    );
};

const TaskHistoryRow: FC<{ homeId: HomeId; task: Task; taskTypeTitleById: Map<string, string> }> = ({
    homeId,
    task,
    taskTypeTitleById,
}) => {
    const onRowClick = useRowClick();
    return (
        <TableRow className="cursor-pointer" onClick={onRowClick(`/directory/homes/${homeId}/tasks/${task.id}`)}>
            <TableCell>{taskTypeTitleById.get(task.taskTypeId) ?? '-'}</TableCell>
            <TableCell>{formatDate(task.taskDate)}</TableCell>
            <TableCell>
                <TaskStatusBadge status={task.status} />
            </TableCell>
            <TableCell>{task.completedAt ? formatDate(task.completedAt) : '-'}</TableCell>
        </TableRow>
    );
};
